import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { DayPicker } from "react-day-picker";
import {
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiFile,
  FiFileText,
  FiImage,
  FiMapPin,
  FiNavigation,
  FiX,
  FiSend,
  FiTruck,
  FiUploadCloud,
} from "react-icons/fi";
import { autocompletePlaces } from "../../services/googlePlacesApi";

const timeOptionsHour = Array.from({ length: 24 }, (_, index) =>
  String(index).padStart(2, "0")
);

const timeOptionsMinute = Array.from({ length: 60 }, (_, index) =>
  String(index).padStart(2, "0")
);

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDistance = (value) =>
  Number(value || 0).toLocaleString("th-TH", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

const formatFileSize = (value) => {
  if (!value) return "-";
  if (value >= 1024 * 1024) {
    return `${(value / (1024 * 1024)).toFixed(2)} MB`;
  }

  return `${Math.ceil(value / 1024)} KB`;
};

const LocationInput = ({
  label,
  name,
  placeholder,
  value,
  onChange,
  suggestions,
  open,
  onSelect,
  disabled,
}) => (
  <div className="relative">
    <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
      {label}
    </label>
    <div className="relative">
      <FiMapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
      <input
        type="text"
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        autoComplete="off"
        className="w-full pl-10 pr-4 py-3 border rounded-xl bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-cyan-400 focus:outline-none transition"
        disabled={disabled}
      />
    </div>

    {open && suggestions.length > 0 && (
      <div className="absolute z-20 mt-2 w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl overflow-hidden">
        {suggestions.map((item) => (
          <button
            key={`${name}-${item.placeId}`}
            type="button"
            onClick={() => onSelect(item.displayName)}
            className="w-full px-4 py-3 text-left hover:bg-cyan-50 dark:hover:bg-cyan-900/20 transition"
          >
            <div className="text-sm font-medium text-gray-800 dark:text-gray-100">
              {item.primaryText}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {item.secondaryText}
            </div>
          </button>
        ))}
      </div>
    )}
  </div>
);

const TravelExpenseForm = ({
  formData,
  setFormData,
  handleChange,
  quotationOptions,
  selectedQuotationOption,
  onQuotationChange,
  loadingQuotations,
  handleSubmit,
  handleEstimate,
  loading,
  estimating,
  estimateResult,
  setEstimateResult,
  receiptPreviewUrls,
  existingReceiptPreviews = [],
  onRemoveExistingReceipt,
  onRemoveSelectedReceipt,
  maxReceipts,
  isTollOnlyRequester = false,
  submitLabel = "Submit",
}) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [originSuggestions, setOriginSuggestions] = useState([]);
  const [destinationSuggestions, setDestinationSuggestions] = useState([]);
  const [openSuggestions, setOpenSuggestions] = useState("");
  const [isDarkMode, setIsDarkMode] = useState(
    document.documentElement.classList.contains("dark")
  );

  useEffect(() => {
    const syncTheme = () => {
      const storedTheme = localStorage.getItem("theme");
      const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      const nextIsDark = storedTheme
        ? storedTheme === "dark"
        : document.documentElement.classList.contains("dark") || prefersDark;

      setIsDarkMode(nextIsDark);
    };

    syncTheme();
    window.addEventListener("theme-change", syncTheme);

    return () => {
      window.removeEventListener("theme-change", syncTheme);
    };
  }, []);

  const selectedDateLabel = useMemo(() => {
    if (!formData.departureDate) return "เลือกวันที่เดินทาง";
    return formData.departureDate.toLocaleDateString("th-TH", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }, [formData.departureDate]);

  const departureTimeParts = useMemo(() => {
    if (!formData.departureTime) {
      return {
        hour: "08",
        minute: "00",
      };
    }

    const [rawHour = "08", rawMinute = "00"] = formData.departureTime.split(":");

    return {
      hour: rawHour,
      minute: rawMinute,
    };
  }, [formData.departureTime]);

  const selectStyles = useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        minHeight: 48,
        borderRadius: 12,
        backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
        borderColor: state.isFocused
          ? "#22d3ee"
          : isDarkMode
          ? "#4b5563"
          : "#d1d5db",
        boxShadow: state.isFocused ? "0 0 0 2px rgba(34,211,238,0.18)" : "none",
        ":hover": {
          borderColor: state.isFocused
            ? "#22d3ee"
            : isDarkMode
            ? "#6b7280"
            : "#9ca3af",
        },
      }),
      valueContainer: (base) => ({
        ...base,
        paddingLeft: 8,
        paddingRight: 8,
      }),
      placeholder: (base) => ({
        ...base,
        color: isDarkMode ? "#9ca3af" : "#6b7280",
      }),
      input: (base) => ({
        ...base,
        color: isDarkMode ? "#f9fafb" : "#111827",
      }),
      singleValue: (base) => ({
        ...base,
        color: isDarkMode ? "#f9fafb" : "#111827",
      }),
      clearIndicator: (base) => ({
        ...base,
        color: isDarkMode ? "#d1d5db" : "#6b7280",
      }),
      dropdownIndicator: (base) => ({
        ...base,
        color: isDarkMode ? "#d1d5db" : "#6b7280",
      }),
      indicatorSeparator: (base) => ({
        ...base,
        backgroundColor: isDarkMode ? "#4b5563" : "#d1d5db",
      }),
      menu: (base) => ({
        ...base,
        zIndex: 40,
        borderRadius: 12,
        overflow: "hidden",
        backgroundColor: isDarkMode ? "#111827" : "#ffffff",
        border: `1px solid ${isDarkMode ? "#374151" : "#e5e7eb"}`,
      }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
          ? isDarkMode
            ? "#164e63"
            : "#cffafe"
          : state.isFocused
          ? isDarkMode
            ? "#1f2937"
            : "#ecfeff"
          : isDarkMode
          ? "#111827"
          : "#ffffff",
        color: isDarkMode ? "#f9fafb" : "#111827",
        cursor: "pointer",
      }),
      noOptionsMessage: (base) => ({
        ...base,
        color: isDarkMode ? "#9ca3af" : "#6b7280",
      }),
      loadingMessage: (base) => ({
        ...base,
        color: isDarkMode ? "#9ca3af" : "#6b7280",
      }),
    }),
    [isDarkMode]
  );

  const handleDateSelect = (date) => {
    setFormData((prev) => ({
      ...prev,
      departureDate: date || null,
    }));
    setShowCalendar(false);
  };

  const updateDepartureTime = (nextParts) => {
    setEstimateResult(null);
    setFormData((prev) => ({
      ...prev,
      departureTime: `${nextParts.hour}:${nextParts.minute}`,
    }));
  };

  useEffect(() => {
    let ignore = false;

    const fetchPlaces = async (keyword, setter) => {
      if (!keyword || keyword.trim().length < 2) {
        setter([]);
        return;
      }

      try {
        const data = await autocompletePlaces(keyword);

        if (ignore) return;

        setter(data);
      } catch (error) {
        console.error("Google Places autocomplete error:", error);
        if (!ignore) {
          setter([]);
        }
      }
    };

    const timer = setTimeout(() => {
      if (openSuggestions === "origin") {
        fetchPlaces(formData.origin, setOriginSuggestions);
      }

      if (openSuggestions === "destination") {
        fetchPlaces(formData.destination, setDestinationSuggestions);
      }
    }, 350);

    return () => {
      ignore = true;
      clearTimeout(timer);
    };
  }, [formData.destination, formData.origin, openSuggestions]);

  return (
    <form id="travel-expense-form" onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.35fr)_minmax(320px,0.9fr)] gap-6">
        <section className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gradient-to-br from-white to-cyan-50/60 dark:from-gray-800 dark:to-gray-800 p-5 sm:p-6">
          <div className="flex items-start gap-3 mb-6">
            <div className="mt-1 flex h-11 w-11 items-center justify-center rounded-2xl bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300">
              <FiNavigation size={20} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                ข้อมูลการเดินทาง
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                ระบุต้นทาง ปลายทาง วันที่เวลา และอ้างอิง QT ก่อนส่งคำขอ
              </p>
            </div>
          </div>

          <div className="space-y-5">
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                QT / โครงการ
              </label>
              <Select
                value={selectedQuotationOption}
                options={quotationOptions}
                isClearable
                isSearchable
                isLoading={loadingQuotations}
                placeholder={
                  loadingQuotations
                    ? "กำลังโหลดรายการ QT..."
                    : "ค้นหาเลข QT หรือชื่อโครงการ"
                }
                noOptionsMessage={() => "ไม่พบรายการ QT"}
                loadingMessage={() => "กำลังโหลดรายการ QT..."}
                styles={selectStyles}
                onChange={onQuotationChange}
                isDisabled={loading}
              />
            </div>

            <LocationInput
              label="ต้นทาง"
              name="origin"
              placeholder="เช่น บ้าน, Office, หรือลูกค้า"
              value={formData.origin}
              onChange={(e) => {
                setOpenSuggestions("origin");
                handleChange(e);
              }}
              suggestions={originSuggestions}
              open={openSuggestions === "origin"}
              onSelect={(value) => {
                setFormData((prev) => ({ ...prev, origin: value }));
                setEstimateResult(null);
                setOriginSuggestions([]);
                setOpenSuggestions("");
              }}
              disabled={loading}
            />

            <LocationInput
              label="ปลายทาง"
              name="destination"
              placeholder="ค้นหาสถานที่ปลายทาง"
              value={formData.destination}
              onChange={(e) => {
                setOpenSuggestions("destination");
                handleChange(e);
              }}
              suggestions={destinationSuggestions}
              open={openSuggestions === "destination"}
              onSelect={(value) => {
                setFormData((prev) => ({ ...prev, destination: value }));
                setEstimateResult(null);
                setDestinationSuggestions([]);
                setOpenSuggestions("");
              }}
              disabled={loading}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative min-w-0">
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                  วันที่เดินทาง
                </label>
                <div className="relative">
                  <FiCalendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
                  <button
                    type="button"
                    onClick={() => setShowCalendar((prev) => !prev)}
                    className="w-full text-left pl-10 pr-4 py-3 border rounded-xl bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-cyan-400 focus:outline-none transition"
                    disabled={loading}
                  >
                    {selectedDateLabel}
                  </button>
                </div>

                {showCalendar && (
                  <div className="absolute z-30 mt-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-xl p-3">
                    <DayPicker
                      mode="single"
                      selected={formData.departureDate}
                      onSelect={handleDateSelect}
                    />
                  </div>
                )}
              </div>

              <div className="min-w-0">
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                  เวลาออกเดินทาง
                </label>
                <div className="relative min-w-0">
                  <FiClock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
                  <div className="grid grid-cols-2 gap-2 pl-10 pr-3 py-2.5 border rounded-xl bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700">
                    <select
                      value={departureTimeParts.hour}
                      onChange={(e) =>
                        updateDepartureTime({
                          ...departureTimeParts,
                          hour: e.target.value,
                        })
                      }
                      disabled={loading}
                      className="w-full min-w-0 bg-transparent text-gray-800 dark:text-gray-100 focus:outline-none [color-scheme:light] dark:[color-scheme:dark]"
                    >
                      {timeOptionsHour.map((hour) => (
                        <option
                          key={hour}
                          value={hour}
                          className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100"
                        >
                          {hour}
                        </option>
                      ))}
                    </select>

                    <select
                      value={departureTimeParts.minute}
                      onChange={(e) =>
                        updateDepartureTime({
                          ...departureTimeParts,
                          minute: e.target.value,
                        })
                      }
                      disabled={loading}
                      className="w-full min-w-0 bg-transparent text-gray-800 dark:text-gray-100 focus:outline-none [color-scheme:light] dark:[color-scheme:dark]"
                    >
                      {timeOptionsMinute.map((minute) => (
                        <option
                          key={minute}
                          value={minute}
                          className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100"
                        >
                          {minute}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                หมายเหตุ <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <FiFileText className="absolute left-3 top-4 text-gray-400" />
                <textarea
                  name="note"
                  rows={4}
                  placeholder="เช่น ไปพบลูกค้า / ประชุมกับลูกค้า / สำรวจหน้างาน"
                  value={formData.note}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 border rounded-xl bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-cyan-400 focus:outline-none transition"
                  disabled={loading}
                />
              </div>
            </div>

            <label className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800 px-4 py-3 cursor-pointer">
              <input
                type="checkbox"
                name="useTollway"
                checked={formData.useTollway}
                onChange={handleChange}
                disabled={loading}
                className="h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
              />
              <span className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                <FiTruck className="text-cyan-600 dark:text-cyan-300" />
                ใช้ทางด่วน
              </span>
            </label>

            <div className="rounded-2xl border border-dashed border-cyan-200 dark:border-cyan-900/50 bg-cyan-50/80 dark:bg-cyan-900/10 p-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {isTollOnlyRequester ? "สิทธิ์การเบิกค่าเดินทาง" : "คำนวณระยะทาง"}
                </div>
                {!isTollOnlyRequester && (
                  <button
                    type="button"
                    onClick={handleEstimate}
                    disabled={loading || estimating}
                    className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition ${
                      loading || estimating
                        ? "bg-cyan-100 dark:bg-cyan-900/20 text-cyan-400 cursor-not-allowed"
                        : "bg-[#00ADB5] hover:bg-[#019C9C] text-white"
                    }`}
                  >
                    <FiNavigation />
                    {estimating ? "กำลังคำนวณ..." : "คำนวณระยะทาง"}
                  </button>
                )}
              </div>

              {!isTollOnlyRequester && estimateResult && (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="rounded-xl bg-white dark:bg-gray-800 p-4 border border-gray-200 dark:border-gray-700">
                    <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Distance
                    </div>
                    <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">
                      {formatDistance(estimateResult.distanceKm)} km
                    </div>
                  </div>
                  <div className="rounded-xl bg-white dark:bg-gray-800 p-4 border border-gray-200 dark:border-gray-700">
                    <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Estimated Amount
                    </div>
                    <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">
                      {formatCurrency(estimateResult.amount)} บาท
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 p-5 sm:p-6">
          <div className="flex items-start gap-3 mb-6">
            <div className="mt-1 flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
              <FiCheckCircle size={20} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                ค่าใช้จ่ายเพิ่มเติม
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                แนบค่าทางด่วนและใบเสร็จประกอบก่อน submit
              </p>
            </div>
          </div>

          <div className="space-y-5">
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                ค่าทางด่วน (บาท)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                name="tollFee"
                placeholder="0.00"
                value={formData.tollFee}
                onChange={handleChange}
                disabled={loading || !formData.useTollway}
                className="w-full px-4 py-3 border rounded-xl bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-emerald-400 focus:outline-none transition disabled:bg-gray-100 disabled:text-gray-400 dark:disabled:bg-gray-800/60"
              />
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                ใบเสร็จทางด่วน{" "}
                {formData.useTollway ? (
                  <span className="text-rose-500">*</span>
                ) : null}
              </label>
              <label
                className={`block rounded-2xl border-2 border-dashed px-4 py-6 text-center transition ${
                  formData.useTollway
                    ? "border-emerald-200 dark:border-emerald-800 bg-white dark:bg-gray-800 cursor-pointer"
                    : "border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800/60 cursor-not-allowed"
                }`}
              >
                <input
                  type="file"
                  name="tollReceipts"
                  accept=".jpg,.jpeg,.png,.webp,.pdf,image/jpeg,image/png,image/webp,application/pdf"
                  multiple
                  onChange={handleChange}
                  disabled={loading || !formData.useTollway}
                  className="hidden"
                />
                <FiUploadCloud className="mx-auto mb-3 text-2xl text-emerald-600 dark:text-emerald-300" />
                <div className="text-sm font-medium text-gray-800 dark:text-gray-100">
                  อัปโหลดใบเสร็จได้สูงสุด {maxReceipts} รูป
                </div>
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {formData.useTollway
                    ? "รองรับ JPG, PNG, WEBP, PDF และต้องแนบอย่างน้อย 1 ไฟล์ก่อนส่ง"
                    : "รองรับ JPG, PNG, WEBP, PDF และแนบได้หลายไฟล์ก่อนส่ง"}
                </div>
              </label>
            </div>

            {existingReceiptPreviews.length > 0 && (
              <div>
                <div className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                  <FiFile />
                  ไฟล์ใบเสร็จปัจจุบัน
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {existingReceiptPreviews.map((item, index) => (
                    <div
                      key={`${item.name}-${item.url}`}
                      className="relative rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-cyan-300 dark:hover:border-cyan-700 transition"
                    >
                      <button
                        type="button"
                        onClick={() => onRemoveExistingReceipt?.(index)}
                        className="absolute right-2 top-2 z-10 inline-flex h-7 w-7 items-center justify-center rounded-full bg-black/70 text-white shadow-sm transition hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-white/70"
                        aria-label={`Remove ${item.name}`}
                      >
                        <FiX size={14} />
                      </button>
                      <a href={item.url} target="_blank" rel="noreferrer">
                      {item.previewUrl ? (
                        <img
                          src={item.previewUrl}
                          alt={item.name}
                          className="h-28 w-full object-cover"
                        />
                      ) : (
                        <div className="h-28 w-full flex items-center justify-center bg-gray-50 dark:bg-gray-900/40 text-gray-500 dark:text-gray-300">
                          <div className="text-center">
                            <FiFile className="mx-auto mb-2 text-2xl" />
                            <div className="text-xs font-medium uppercase">
                              {item.type === "pdf" ? "PDF" : "FILE"}
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="px-3 py-2">
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {item.name}
                        </div>
                        <div className="mt-1 text-[11px] text-cyan-600 dark:text-cyan-300">
                          คลิกเพื่อเปิดไฟล์เดิม
                        </div>
                      </div>
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {receiptPreviewUrls.length > 0 && (
              <div>
                <div className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                  <FiImage />
                  ไฟล์ใบเสร็จที่เลือก
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {receiptPreviewUrls.map((item, index) => (
                    <div
                      key={`${item.name}-${item.size}`}
                      className="relative rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                    >
                      <button
                        type="button"
                        onClick={() => onRemoveSelectedReceipt?.(index)}
                        className="absolute right-2 top-2 z-10 inline-flex h-7 w-7 items-center justify-center rounded-full bg-black/70 text-white shadow-sm transition hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-white/70"
                        aria-label={`Remove ${item.name}`}
                      >
                        <FiX size={14} />
                      </button>
                      {item.url ? (
                        <img
                          src={item.url}
                          alt={item.name}
                          className="h-28 w-full object-cover"
                        />
                      ) : (
                        <div className="h-28 w-full flex items-center justify-center bg-gray-50 dark:bg-gray-900/40 text-gray-500 dark:text-gray-300">
                          <div className="text-center">
                            <FiFile className="mx-auto mb-2 text-2xl" />
                            <div className="text-xs font-medium uppercase">PDF</div>
                          </div>
                        </div>
                      )}
                      <div className="px-3 py-2">
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {item.name}
                        </div>
                        <div className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
                          {formatFileSize(item.size)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4 space-y-3">
              <div className="flex items-center justify-between gap-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  ทางด่วน
                </span>
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  {formatCurrency(formData.useTollway ? formData.tollFee : 0)} บาท
                </span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  ค่าเดินทาง
                </span>
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  {isTollOnlyRequester
                    ? "0.00 บาท"
                    : estimateResult
                    ? `${formatCurrency(estimateResult.amount)} บาท`
                    : "-"}
                </span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  อ้างอิงโครงการ
                </span>
                <span className="text-right font-semibold text-gray-900 dark:text-gray-100">
                  {formData.projectName || "-"}
                </span>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-medium transition ${
                loading
                  ? "bg-cyan-100 dark:bg-cyan-900/20 text-cyan-400 cursor-not-allowed"
                  : "bg-[#00ADB5] hover:bg-[#019C9C] text-white"
              }`}
            >
              <FiSend />
              {loading ? "Submitting..." : submitLabel}
            </button>
          </div>
        </section>
      </div>
    </form>
  );
};

export default TravelExpenseForm;
